import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Crosshair, Target, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL.replace(/\/$/, '')}/api`;

// Operating window: 10:00-21:00, 30-min slots => 22 slots / zi
const SLOTS_PER_DAY = 22;

const MONTH_NAMES_RO = [
  'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
];
const WEEKDAYS_RO = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'];

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const minutesBetween = (a, b) => Math.max(0, Math.round((b - a) / 60000));

// Compute occupancy ratio (0..1) for a given date based on blocked slots
const occupancyForDate = (dateStr, blocks) => {
  const dayBlocks = blocks.filter(b => b.date_iso === dateStr);
  if (dayBlocks.length === 0) return 0;
  // Sum total minutes blocked, capped at full day (22 * 30 = 660 min)
  let totalMin = 0;
  for (const b of dayBlocks) {
    const start = new Date(b.start_datetime);
    const end = new Date(b.end_datetime);
    totalMin += minutesBetween(start, end);
  }
  const totalDayMinutes = SLOTS_PER_DAY * 30;
  return Math.min(1, totalMin / totalDayMinutes);
};

const levelForOccupancy = (occ) => {
  if (occ <= 0) return 0;          // free
  if (occ < 0.34) return 1;        // light
  if (occ < 0.67) return 2;        // medium
  if (occ < 1) return 3;           // heavy
  return 4;                        // full
};

const LEVEL_COLORS = {
  0: { bg: 'rgba(34, 197, 94, 0.18)', border: 'rgba(34, 197, 94, 0.45)', text: '#bbf7d0', label: 'Liber' },
  1: { bg: 'rgba(234, 179, 8, 0.18)', border: 'rgba(234, 179, 8, 0.45)', text: '#fde68a', label: 'Câteva ocupate' },
  2: { bg: 'rgba(249, 115, 22, 0.22)', border: 'rgba(249, 115, 22, 0.55)', text: '#fed7aa', label: 'Pe jumătate' },
  3: { bg: 'rgba(239, 68, 68, 0.28)', border: 'rgba(239, 68, 68, 0.65)', text: '#fecaca', label: 'Aproape plin' },
  4: { bg: 'rgba(127, 29, 29, 0.65)', border: '#7f1d1d', text: '#fca5a5', label: 'Plin' },
};

const OccupancyCalendar = ({ onDateSelect, externalGameType, onGameTypeChange, selectedDate }) => {
  // If parent provides onGameTypeChange, the calendar is a controlled component
  // (parent owns the state). Otherwise the calendar manages its own state.
  const isControlled = typeof onGameTypeChange === 'function';
  const [internalGameType, setInternalGameType] = useState(externalGameType || 'lasertag');
  const gameType = isControlled ? (externalGameType || 'lasertag') : internalGameType;
  const setGameType = (gt) => {
    if (isControlled) onGameTypeChange(gt);
    else setInternalGameType(gt);
  };
  const [monthAnchor, setMonthAnchor] = useState(startOfMonth(new Date()));
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/reservations/blocked-times`, { params: { game_type: gameType } })
      .then(r => setBlocks(r.data?.data || []))
      .catch(() => setBlocks([]))
      .finally(() => setLoading(false));
  }, [gameType]);

  // Refresh occasionally so a freshly-blocked slot shows up without a manual reload
  useEffect(() => {
    const id = setInterval(() => {
      axios.get(`${API}/reservations/blocked-times`, { params: { game_type: gameType } })
        .then(r => setBlocks(r.data?.data || []))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [gameType]);

  const monthDays = useMemo(() => {
    const days = [];
    const first = startOfMonth(monthAnchor);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    // pad leading days to start the week on Monday
    const firstWeekday = (first.getDay() + 6) % 7; // 0 = Mon, 6 = Sun
    for (let i = 0; i < firstWeekday; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      const dt = new Date(first.getFullYear(), first.getMonth(), d);
      days.push(dt);
    }
    return days;
  }, [monthAnchor]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const goPrev = () => setMonthAnchor(addMonths(monthAnchor, -1));
  const goNext = () => setMonthAnchor(addMonths(monthAnchor, 1));

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div style={styles.toggle}>
          <button
            style={gameType === 'lasertag' ? styles.toggleBtnActive : styles.toggleBtn}
            onClick={() => setGameType('lasertag')}
            type="button"
          >
            <Crosshair size={16} style={{ marginRight: 6 }} /> Lasertag
          </button>
          <button
            style={gameType === 'paintball' ? styles.toggleBtnActive : styles.toggleBtn}
            onClick={() => setGameType('paintball')}
            type="button"
          >
            <Target size={16} style={{ marginRight: 6 }} /> Paintball
          </button>
        </div>
        <div style={styles.monthNav}>
          <button style={styles.navBtn} onClick={goPrev} type="button" aria-label="Luna anterioară">
            <ChevronLeft size={20} />
          </button>
          <div style={styles.monthLabel}>
            {MONTH_NAMES_RO[monthAnchor.getMonth()]} {monthAnchor.getFullYear()}
          </div>
          <button style={styles.navBtn} onClick={goNext} type="button" aria-label="Luna următoare">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div style={styles.weekdays}>
        {WEEKDAYS_RO.map(w => <div key={w} style={styles.weekday}>{w}</div>)}
      </div>

      <div style={styles.grid}>
        {monthDays.map((d, idx) => {
          if (!d) return <div key={`pad-${idx}`} style={styles.padCell} />;
          const dateStr = isoDate(d);
          const isPast = d < today;
          const isToday = isoDate(today) === dateStr;
          const isSelected = selectedDate === dateStr;
          const occ = isPast ? 0 : occupancyForDate(dateStr, blocks);
          const level = isPast ? -1 : levelForOccupancy(occ);
          const colors = level >= 0 ? LEVEL_COLORS[level] : { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)', text: '#666' };

          const cellStyle = {
            ...styles.cell,
            background: colors.bg,
            borderColor: isSelected ? '#d9fb06' : isToday ? '#fff' : colors.border,
            color: colors.text,
            cursor: isPast || level === 4 ? 'not-allowed' : 'pointer',
            opacity: isPast ? 0.4 : 1,
            outline: isSelected ? '2px solid #d9fb06' : 'none',
          };

          return (
            <button
              key={dateStr}
              type="button"
              disabled={isPast}
              onClick={() => !isPast && onDateSelect && onDateSelect(dateStr, gameType)}
              style={cellStyle}
              title={`${dateStr} — ${level >= 0 ? LEVEL_COLORS[level].label : 'Trecut'}`}
            >
              <div style={styles.cellDay}>{d.getDate()}</div>
              {level > 0 && (
                <div style={styles.cellLabel}>{LEVEL_COLORS[level].label}</div>
              )}
            </button>
          );
        })}
      </div>

      <div style={styles.legend}>
        {[0, 1, 2, 3, 4].map(l => (
          <div key={l} style={styles.legendItem}>
            <span style={{ ...styles.legendDot, background: LEVEL_COLORS[l].bg, border: `1px solid ${LEVEL_COLORS[l].border}` }} />
            {LEVEL_COLORS[l].label}
          </div>
        ))}
        {loading && <span style={{ marginLeft: 'auto', color: '#aaa', fontSize: '0.85rem' }}>Se actualizează...</span>}
      </div>
    </div>
  );
};

const styles = {
  wrap: {
    background: 'rgba(0,0,0,0.35)',
    border: '1px solid rgba(217, 251, 6, 0.2)',
    borderRadius: 14,
    padding: 20,
    margin: '0 auto',
    maxWidth: 980,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  toggle: { display: 'flex', gap: 6, background: 'rgba(0,0,0,0.4)', padding: 4, borderRadius: 10 },
  toggleBtn: {
    display: 'flex', alignItems: 'center',
    padding: '8px 14px', border: 'none', borderRadius: 8,
    background: 'transparent', color: '#bbb', cursor: 'pointer', fontWeight: 600,
  },
  toggleBtnActive: {
    display: 'flex', alignItems: 'center',
    padding: '8px 14px', border: 'none', borderRadius: 8,
    background: '#d9fb06', color: '#000', cursor: 'pointer', fontWeight: 700,
  },
  monthNav: { display: 'flex', alignItems: 'center', gap: 10 },
  navBtn: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8, padding: 6, color: '#eee', cursor: 'pointer', display: 'flex',
  },
  monthLabel: { fontSize: '1.1rem', fontWeight: 700, color: '#fff', minWidth: 180, textAlign: 'center' },
  weekdays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4,
    marginBottom: 6,
  },
  weekday: {
    textAlign: 'center', padding: '6px 0',
    color: '#aaa', fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.05em',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 6,
  },
  padCell: { background: 'transparent', minHeight: 64 },
  cell: {
    minHeight: 64,
    border: '1.5px solid',
    borderRadius: 8,
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    fontWeight: 600,
    fontSize: '0.95rem',
    transition: 'transform 0.06s',
  },
  cellDay: { fontSize: '1rem', lineHeight: 1 },
  cellLabel: { fontSize: '0.65rem', alignSelf: 'flex-end', opacity: 0.95, fontWeight: 500, textTransform: 'lowercase' },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 14,
    fontSize: '0.82rem',
    color: '#ccc',
    alignItems: 'center',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6 },
  legendDot: { display: 'inline-block', width: 16, height: 16, borderRadius: 4 },
};

export default OccupancyCalendar;
